import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { AppService } from '@core/services/app/app-api';
import { LanguageService } from '@core/services/language/language-api';
import { LoadingService } from '@core/services/loading/loading-api';
import { OrgAndStockroomService } from '@core/services/org-and-stockroom/org-and-stockroom-api';
import { ToastService } from '@core/services/toast-api';
import { TokenHolderService } from '@core/services/token-holder/token-holder-api';
import { AppState } from '@core/store/reducers/common.reducer';
import { getFetchingUserData, getUserConfig } from '@core/store/selectors/common.selectors';
import { Store } from '@ngrx/store';
import { User } from '@shared/models/user.model';
import { Subscription } from 'rxjs';
import { SharedModule } from '@shared/shared.module';
import { FormsModule } from '@angular/forms';
import { MaterialsModule } from '@shared/materials.module';

@Component({
  selector: 'app-avantor-splash-screen',
  templateUrl: './avantor-splash-screen.html',
  styleUrls: ['./avantor-splash-screen.scss'],
  standalone: true,
  imports: [CommonModule, SharedModule, FormsModule, MaterialsModule],
})
export class AvantorSplashScreenComponent implements OnInit, OnDestroy {
  public _showLoading: boolean = true;   // show spinner by default until ready
  public isLoadingOrgs: boolean = false;
  public userData: any;
  public currentUser: any;
  configSub!: Subscription;
  fetchingUserData: any = false;
  initSessionAccountWorked: any = false;

  constructor(
    private auth0: AuthService,
    private appService: AppService,
    private toastService: ToastService,
    private languageService: LanguageService,
    private tokenHolderService: TokenHolderService,
    private orgAndStockRoomService: OrgAndStockroomService,
    private loadingService: LoadingService,
    private router: Router,
    private store: Store<AppState>
  ) { }

  ngOnInit(): void {
    this.initStore();

    // FIX: The original logic was inverted.
    // loadingStatus=true  means an HTTP request is in flight → show spinner
    // loadingStatus=false means all requests done           → hide spinner
    // The HTML uses @if (_showLoading) so _showLoading must MATCH loadingStatus.
    this.loadingService.isLoading$.subscribe((loadingStatus: boolean) => {
      this._showLoading = loadingStatus;
    });

    this.initSessionAuth();
  }

  initStore() {
    this.store.select(getFetchingUserData).subscribe((res: any) => {
      this.fetchingUserData = res;
    });

    this.configSub = this.store.select(getUserConfig).subscribe((res: any) => {
      this.currentUser = res;
      // Only proceed when we have actual user data (not the initial empty {})
      if (this.currentUser && Object.keys(this.currentUser).length > 0) {
        this.initSessionAccount();
      }
    });
  }

  /**
   * @INIT_SESSION_TOKEN
   *
   * Waits for Auth0 SDK to finish processing the callback before checking tokens.
   * isLoading$=true  → SDK still processing → wait
   * isLoading$=false → SDK done, tokens now available via idTokenClaims$
   */
  private initSessionAuth() {
    this.auth0.isLoading$.subscribe((isLoading) => {

      // Wait until Auth0 has finished processing
      if (isLoading) {
        return;
      }

      const existingToken = this.tokenHolderService.getAuthToken();

      if (!existingToken) {
        // No token yet — check if Auth0 just completed a login callback
        this.auth0.idTokenClaims$.subscribe(async (tokenClaims) => {
          if (tokenClaims) {
            // Coming back from Auth0 with fresh token
            this.isLoadingOrgs = true;
            this.tokenHolderService.setAuthToken(tokenClaims.__raw);
            await this.initSessionAccount();
          } else {
            // No token and no claims — not authenticated.
            // Guard against calling loginWithRedirect() while Auth0 is still
            // processing the callback URL params (would cause redirect loop).
            const urlParams = new URLSearchParams(window.location.search);
            const isAuth0Callback = urlParams.has('code') || urlParams.has('state');
            if (!isAuth0Callback) {
              sessionStorage.removeItem('config');
              this.auth0.loginWithRedirect();
            }
          }
        });
      } else {
        // Token exists — check expiry
        if (this.tokenHolderService.validateSessionToken()) {
          // Token expired — re-authenticate
          sessionStorage.removeItem('config');
          this.auth0.loginWithRedirect();
        } else {
          // Valid token — proceed
          if (!this.isLoadingOrgs) {
            this.isLoadingOrgs = true;
            this.initSessionAccount();
          }
        }
      }
    });
  }

  /**
   * @INIT_SESSION_ACCOUNT
   * Loads user data, sets privileges and navigates to correct route.
   */
  private async initSessionAccount() {
    if (!this.fetchingUserData && !this.initSessionAccountWorked) {
      this.initSessionAccountWorked = true;

      if (!this.orgAndStockRoomService.getStockroomAndOrg()) {
        this.orgAndStockRoomService.setStockroomAndOrg('', '');
      }

      if (!this.tokenHolderService.getAuthToken()) {
        return;
      }

      // FIX: Guard against null/undefined before calling Object.keys()
      const hasStoredUserData = this.currentUser &&
        typeof this.currentUser === 'object' &&
        Object.keys(this.currentUser).length > 0;

      if (hasStoredUserData) {
        // User data already in store (e.g. page refresh with hydrated state)
        this.userData = this.currentUser;
        this.updateDetailsAfterGetUser(this.userData);
      } else {
        // Fresh login — fetch from API
        this.appService
          .initUserData()
          .then((userData) => {
            this.updateDetailsAfterGetUser(userData);
          })
          .catch((res) => {
            if (res?.error?.errorCode === '401') {
              this.toastService.show(
                'error',
                'Server Error',
                `This user account has an unauthorized default im-context. Please wait while we redirect you to login page`,
                false,
                5000
              );
              this.userTrackingLogout();
            } else if (res?.error?.errorCode === '404') {
              this.router.navigate(['/access-denied']);
            }
          });
      }
    }
  }

  private initTranslationPreference(userData: User) {
    this.languageService.translateLanguage(userData.language);
  }

  private updateDetailsAfterGetUser(userData: any) {
    this.initTranslationPreference(userData);
    this.appService.setUserData(userData);
    this.orgAndStockRoomService.autoNavigateStockroomOrg();
  }

  userTrackingLogout() {
    const userTrackingInfo = sessionStorage.getItem('userTrackingInfo');
    this.orgAndStockRoomService.auditLogout().then((data: any) => {
      if (userTrackingInfo) {
        sessionStorage.removeItem('userTrackingInfo');
      }
      setTimeout(() => {
        this.tokenHolderService.logoutSession();
      }, 5000);
    }).catch(err => {
      setTimeout(() => {
        this.tokenHolderService.logoutSession();
      }, 5000);
      console.log(err);
    });
  }

  ngOnDestroy(): void {
    this.configSub.unsubscribe();
  }
}